<template>
  <div class="force-task-detail">
    <el-form
      ref="formRef"
      :model="state.taskInfo"
      :rules="rules"
      label-width="132px"
      size="default"
      status-icon
      require-asterisk-position="right"
      label-position="left"
      :disabled="disabled"
    >
      <el-form-item label="Site" prop="siteProjectId">
        <DropdownSelect
          v-model="state.taskInfo.siteProjectId"
          :options="state.dropdownSite"
          value-key="siteProjectId"
          label-key="siteName"
          description-key="siteProjectId"
          placeholder="Select Site"
          :loading="state.loadingSite"
          :remote="true"
          :remote-method="searchSites"
          :show-description="true"
          @change="setSite"
          @visible-change="onSiteVisibleChange"
        />
      </el-form-item>

      <el-form-item label="Overhead" prop="overHead">
        <div class="flex justify-end w-[400px]">
           <el-switch
             v-model="state.taskInfo.overHead"
             size="large"
             @change="onOverheadChange"
           />
        </div>
      </el-form-item>

      <!-- WBS field (hidden when overhead=true) -->
      <el-form-item v-if="!state.taskInfo.overHead" label="Job/Project" prop="jobProjectId">
        <DropdownSelect
          v-model="state.taskInfo.jobProjectId"
          :options="state.dropdownJob"
          value-key="jobProjectId"
          label-key="jobName"
          description-key="jobProjectId"
          placeholder="Select Job/Project"
          :loading="state.loadingJob"
          :remote="true"
          :remote-method="searchProjects"
          :show-description="true"
          @change="setJob"
          @visible-change="onJobVisibleChange"
        />
      </el-form-item>

      <!-- Activity field (hidden when overhead=true) -->
      <el-form-item v-if="!state.taskInfo.overHead" label="Code/Task" prop="activityId">
        <DropdownSelect
          v-model="state.taskInfo.activityId"
          :options="state.dropdownActivity"
          value-key="activityId"
          label-key="activityName"
          description-key="activityId"
          placeholder="Select Code/Task"
          :loading="state.loadingActivity"
          :disabled="!!state.taskInfo.siteProjectId && !state.taskInfo.jobProjectId"
          :remote="true"
          :remote-method="searchActivities"
          @change="setActivity"
          @visible-change="onActivityVisibleChange"
        />
      </el-form-item>

      <!-- Code/Task field (only shown when overhead=true) -->
      <el-form-item v-else label="Code/Task" prop="npCode">
        <DropdownSelect
          v-model="state.taskInfo.npCode"
          :options="state.dropdownTask"
          value-key="npCode"
          label-key="npCodeName"
          description-key="description"
          placeholder="Select Code/Task"
          :loading="state.loadingTask"
          :remote="true"
          :remote-method="searchTasks"
          @change="setTask"
          @visible-change="onTaskVisibleChange"
        />
      </el-form-item>

      <el-form-item class="task-description" label="Task Description" prop="comments">
        <el-input
          v-model="state.taskInfo.comments"
          :rows="2"
          type="textarea"
          class="w-[400px]"
          placeholder="Enter Description"
          size="large"
          @input="onTaskDescriptionInput"
        />
      </el-form-item>

      <div v-if="allowInputFieldService">
        <el-form-item label="Field Service" prop="additionInfo">
          <div class="flex justify-end w-[400px]">
             <el-switch
               v-model="state.taskInfo.additionInfo"
               size="large"
               @change="onFieldServiceChange"
             />
          </div>
        </el-form-item>

        <template v-if="state.taskInfo.additionInfo">
          <FieldServiceForm
            ref="fieldServiceFormRef"
            :model-value="state.taskInfo"
            :sig-option="sigOption"
            :disabled="props.disabled"
            @update:field="handleFieldServiceUpdate"
          />
        </template>
      </div>
    </el-form>

    <ConfirmDialog
      v-model:visible="showOverheadConfirm"
      title="Switching to Overhead"
      message="All current task data will be cleared.&#10;Do you want to continue?"
      lblBtnSubmit="Continue"
      lblBtnCancel="Cancel"
      iconBtnName="check-mark-circle"
      iconPopup="warring"
      :width="400"
      @submit="handleOverheadConfirm"
      @cancel="handleOverheadCancel"
    />
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, onMounted } from "vue";
import useForceTaskDetail from "@/composables/v2/useForceTaskDetail";
import ConfirmDialog from "@/components/v2/ConfirmDialog.vue";
import FieldServiceForm from "@/components/v2/FieldServiceForm.vue";
import DropdownSelect from "@/components/v2/DropdownSelect.vue";
import type { FieldServiceInfo } from "@/composables/v2/useFieldService";
import { useOverheadConfirmation } from "@/composables/v2/useOverheadConfirmation";
import { useFormHelpers } from "@/composables/v2/useFormHelpers";
import { COMPANY_IDS } from "@/composables/v2/types";

const props = defineProps<{ 
  taskId: string;
  initialData?: any;
  isEditMode?: boolean;
  disabled?: boolean;
}>();
const {
  state,
  rules,
  allowInputFieldService,
  sigOption,
  loadSitesFromApi,
  searchSites,
  loadProjectsFromApi,
  searchProjects,
  loadOverheadTasksFromApi,
  searchTasks,
  loadActivitiesFromApi,
  searchActivities,
  setActivity,
  setSite,
  setJob,
  setTask,
  setTaskDescription,
  setFieldService,
  setOverhead,
  uploadSignatures,
  // Inject functions for edit mode
  injectJobIfNeeded,
  injectActivityIfNeeded,
  injectSiteIfNeeded,
} = useForceTaskDetail();

// Ref to FieldServiceForm component for signature access
const fieldServiceFormRef = ref();

// Load initial data for edit mode
const loadInitialData = async (data: any) => {
  if (!data) return;
  
  // Map API response fields to state (handle both PascalCase and camelCase)
  state.taskInfo.siteProjectId = data.SiteProjectID || data.siteProjectId || undefined;
  state.taskInfo.siteName = data.SiteName || data.siteName || undefined;
  state.taskInfo.jobProjectId = data.JobProjectID || data.jobProjectId || undefined;
  state.taskInfo.jobName = data.JobName || data.jobName || undefined;
  state.taskInfo.activityId = data.ActivityId || data.activityId || undefined;
  state.taskInfo.activityName = data.ActivityName || data.activityName || undefined;
  state.taskInfo.npCode = data.NPCode || data.npCode || undefined;
  state.taskInfo.comments = data.Comments || data.comments || '';
  state.taskInfo.overHead = data.OverHead ?? data.overHead ?? false;
  state.taskInfo.additionInfo = data.FieldService ?? data.fieldService ?? false;
  
  // Map additional info from TimesheetAdditionals
  const additional = data.TimesheetAdditionals?.[0] || data.timesheetAdditionals?.[0];
  if (additional) {
    state.taskInfo.manufacturer = additional.Manufacturer || additional.manufacturer || '';
    state.taskInfo.model = additional.Model || additional.model || '';
    state.taskInfo.SMUHour = additional.Smureading || additional.smureading || 0;
    state.taskInfo.serialNo = additional.SerialNumber || additional.serialNumber || '';
    state.taskInfo.clientName = additional.CustomerName || additional.customerName || '';
    state.taskInfo.customerEmail = additional.CustomerEmail || additional.customerEmail || '';
    state.taskInfo.location = additional.Locations || additional.locations || '';
    state.taskInfo.travelKMS = additional.TravelKms || additional.travelKms || 0;
    state.taskInfo.vehRegNo = additional.Rego || additional.rego || '';
    state.taskInfo.mechanicalAcceptance = additional.WorkerSignature || additional.workerSignature || '';
    state.taskInfo.customerAcceptance = additional.CustomerSignature || additional.customerSignature || '';
    state.taskInfo.fieldServiceNote = additional.AdditionalInfo || additional.additionalInfo || '';
    state.taskInfo.customerAcceptanceName = additional.CustomerAcceptanceName || additional.customerAcceptanceName || '';
    
    // Set validation flags for existing signatures so validation passes
    // 'existing' indicates the signature was loaded from server (not drawn)
    if (state.taskInfo.customerAcceptance) {
      state.taskInfo.isCustomerAcceptance = 'existing';
    }
    if (state.taskInfo.mechanicalAcceptance) {
      state.taskInfo.isMechanicalAcceptance = 'existing';
    }
  }
  
  // Preload dropdown data for existing values
  await preloadDropdowns(data);
};

// Preload dropdown data based on existing values
const preloadDropdowns = async (data: any) => {
  const siteId = data.SiteProjectID || data.siteProjectId;
  const jobId = data.JobProjectID || data.jobProjectId;
  const activityId = data.ActivityId || data.activityId;
  const isOverhead = data.OverHead ?? data.overHead ?? false;
  const isFieldService = data.FieldService ?? data.fieldService ?? false;
  
  try {
    // 1. Inject specific items first to ensure we have the real names
    if (siteId) {
      const injectedSite = await injectSiteIfNeeded(siteId);
      if (injectedSite) {
        state.taskInfo.siteName = injectedSite.siteName;
      }
    }
    
    if (jobId) {
      const injectedJob = await injectJobIfNeeded(siteId, jobId);
      if (injectedJob) {
        state.taskInfo.jobName = injectedJob.jobName;
      }
    }

    // 2. Load general dropdown lists
    await loadSitesFromApi();
    
    if (siteId) {
      await loadProjectsFromApi(siteId);
    }
    
    // Load overhead tasks if in overhead mode
    // For non-overhead: load activities and inject if needed
    if (isOverhead) {
      await loadOverheadTasksFromApi();
    } else if (jobId) {
      // Load activities for the job
      await loadActivitiesFromApi(jobId);
      
      // Inject activity if not in list
      if (activityId) {
        const injectedActivity = await injectActivityIfNeeded(jobId, activityId);
        if (injectedActivity) {
          state.taskInfo.activityName = injectedActivity.activityName;
        }
      }
    }
    
    if (isFieldService) {
      // Field service fields are free-entry under the standard flow.
    }
  } catch (err) {
    // Error loading dropdowns
  }
};

const loadedDataId = ref<string | null>(null);
watch(() => props.initialData, (newData) => {
  // For copied data, use a unique identifier to ensure it always loads
  const isCopy = newData?.isCopy ?? false;
  const dataId = isCopy 
    ? `copy-${Date.now()}-${JSON.stringify(newData?.SiteProjectID)}`
    : (newData?.TimesheetLineId || newData?.id || JSON.stringify(newData?.SiteProjectID));
  
  if (loadedDataId.value === dataId && dataId && !isCopy) {
    return;
  }
  
  if (newData) {
    loadedDataId.value = dataId;
    loadInitialData(newData);
  }
}, { immediate: true });

const handleFieldServiceUpdate = (field: keyof FieldServiceInfo, value: any) => {
  (state.taskInfo as any)[field] = value;
};

const onSiteVisibleChange = (visible: boolean) => {
  if (visible) {
    loadSitesFromApi();
  }
};

const onOverheadChange = (val: any) => {
  handleOverheadChange(!!val);
};

const onJobVisibleChange = (visible: boolean) => {
  if (visible) {
    loadProjectsFromApi(state.taskInfo.siteProjectId);
  }
};

const onActivityVisibleChange = (visible: boolean) => {
  if (visible) {
    loadActivitiesFromApi();
  }
};

const onTaskVisibleChange = (visible: boolean) => {
  if (visible) {
    loadOverheadTasksFromApi();
  }
};

const onTaskDescriptionInput = (value: string) => {
  setTaskDescription(value);
};

const onFieldServiceChange = (val: any) => {
  setFieldService(!!val);
  if (formRef.value && typeof formRef.value.clearValidate === "function") {
    formRef.value.clearValidate();
  }
};

const formRef = ref();

const resetForceSpecificFields = () => {
  // Clear Job/Project (WBS) field
  state.taskInfo.jobProjectId = undefined;
  state.taskInfo.jobName = undefined;
  
  // Clear Activity field
  state.taskInfo.activityId = undefined;
  state.taskInfo.activityName = undefined;
  
  // Clear Code/Task (NP Code) field
  state.taskInfo.npCode = undefined;
  state.taskInfo.npCodeName = undefined;
  
  // Clear dropdown lists
  state.dropdownTask = [];
  state.dropdownActivity = [];
  
  // Note: siteProjectId is kept as it's common for both modes
  state.taskInfo.comments = "";
};

const {
  showOverheadConfirm,
  handleOverheadChange,
  handleOverheadConfirm,
  handleOverheadCancel,
} = useOverheadConfirmation(state, setOverhead, resetForceSpecificFields);

const { hasData } = useFormHelpers(formRef, state);

const getTaskData = () => {
  const taskInfo = state.taskInfo;
  
  let timesheetAdditional = null;
  if (taskInfo.additionInfo) {
    timesheetAdditional = {
      manufacturer: taskInfo.manufacturer || '',
      model: taskInfo.model || '',
      smureading: taskInfo.SMUHour || 0,
      serialNumber: taskInfo.serialNo || '',
      customerName: taskInfo.clientName || '',
      customerEmail: taskInfo.customerEmail || '',
      locations: taskInfo.location || '',
      travelKms: taskInfo.travelKMS || 0,
      rego: taskInfo.vehRegNo || '',
      workerSignature: taskInfo.mechanicalAcceptance || '',
      customerSignature: taskInfo.customerAcceptance || '',
      additionalInfo: taskInfo.fieldServiceNote || '',
      customerAcceptanceName: taskInfo.customerAcceptanceName || ''
    };
  }

  return {
    comments: taskInfo.comments || '',
    overHead: taskInfo.overHead || false,
    companyId: COMPANY_IDS.FORCE,
    siteProjectId: taskInfo.siteProjectId || null,
    jobProjectId: taskInfo.jobProjectId || null,
    activityId: !taskInfo.overHead ? (taskInfo.activityId || null) : null,
    npCode: taskInfo.overHead ? (taskInfo.npCode || null) : null,
    fieldService: taskInfo.additionInfo || false,
    timesheetAdditional: timesheetAdditional
  };
};

// Get task data with uploaded signatures (call this before submit)
const getTaskDataWithSignatures = async () => {
  // Upload signatures if field service is enabled and signatures were drawn
  if (state.taskInfo.additionInfo) {
    const customerRef = fieldServiceFormRef.value?.signatureCustomer;
    const mechanicalRef = fieldServiceFormRef.value?.signatureMechanical;
    await uploadSignatures(customerRef, mechanicalRef);
  }
  return getTaskData();
};

defineExpose({
  formRef,
  taskId: props.taskId,
  hasData,
  getTaskData,
  getTaskDataWithSignatures,
  loadInitialData,
  fieldServiceFormRef
});
</script>

<style lang="scss">
.force-task-detail {
  .customer-acceptance-name {
    label {
      line-height: 20px !important;
    }
  }

  .sign-pad {
    border: 1px solid #dcdfe6;
    border-radius: 4px;
  }

  .is-error {
    .sign-pad {
      border-color: #f56c6c !important;
    }
  }

  .task-description {
    .el-form-item__label {
      line-height: 20px !important;
    }

    textarea {
      min-height: 100px !important;
    }
  }
}
</style>
